import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiCheckCircle, FiAlertTriangle, FiAlertOctagon, FiCheckSquare } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const ThreeWayMatchingAuditReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("ThreeWayMatch");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const fullMatchCount = data.filter((r) => r.matchStatus === "Full Match").length;
    const qtyDiscrepancyCount = data.filter((r) => r.matchStatus === "Quantity Discrepancy").length;
    const rateAlertCount = data.filter((r) => r.matchStatus === "Rate Variance Alert").length;
    const matchPercentage = data.length ? ((fullMatchCount / data.length) * 100).toFixed(1) : 0;

    return {
      totalAudited: data.length,
      fullMatchCount,
      matchPercentage,
      qtyDiscrepancyCount,
      rateAlertCount,
    };
  }, [data]);

  const uniqueVendors = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.vendorName).filter(Boolean)));
  }, [data]);

  const getMatchChipType = (status) => {
    if (status === "Full Match") {
      return "success";
    }
    if (status === "Quantity Discrepancy") {
      return "warning";
    }
    if (status === "Rate Variance Alert") {
      return "error";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Vendor Name",
        field: "vendorName",
        colId: "vendorName",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "PO Reference & Rate",
        field: "poReference",
        colId: "poReference",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => (
          <div>
            <span
              style={{
                fontWeight: 700,
                fontFamily: "monospace",
                color: "var(--color-primary, #0b6462)",
              }}
            >
              {p.value}
            </span>
            <div style={{ color: "#475569", fontSize: "11px" }}>
              Agreed: PKR {Number(p.data?.poUnitRate).toLocaleString()}
            </div>
          </div>
        ),
      },
      {
        headerName: "GRN & Delivered Qty",
        field: "grnReference",
        colId: "grnReference",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => (
          <div>
            <span
              style={{
                fontWeight: 700,
                fontFamily: "monospace",
                color: "#0369a1",
              }}
            >
              {p.value}
            </span>
            <div style={{ color: "#15803d", fontWeight: 600, fontSize: "11px" }}>
              Delivered: {Number(p.data?.grnDeliveredQty).toLocaleString()}
            </div>
          </div>
        ),
      },
      {
        headerName: "Vendor Invoice & Rate",
        field: "vendorInvoiceNo",
        colId: "vendorInvoiceNo",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => (
          <div>
            <span
              style={{
                fontWeight: 700,
                fontFamily: "monospace",
                color: "#4338ca",
              }}
            >
              {p.value}
            </span>
            <div style={{ color: "#64748b", fontSize: "11px" }}>
              Billed: {Number(p.data?.invoicedQty).toLocaleString()} @ PKR{" "}
              {Number(p.data?.billedUnitRate).toLocaleString()}
            </div>
          </div>
        ),
      },
      {
        headerName: "Quantity Variance",
        field: "quantityVariance",
        colId: "quantityVariance",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => {
          const val = p.value || 0;
          if (val === 0) {
            return (
              <span
                style={{
                  background: "#f0fdf4",
                  color: "#16a34a",
                  padding: "2px 8px",
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                Exact Match (0)
              </span>
            );
          }
          return (
            <span
              style={{
                background: "#fef2f2",
                color: "#dc2626",
                padding: "2px 8px",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "12px",
              }}
            >
              {val > 0 ? `+${val.toLocaleString()}` : val.toLocaleString()}
            </span>
          );
        },
      },
      {
        headerName: "Price Variance",
        field: "priceVariance",
        colId: "priceVariance",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => {
          const val = p.value || 0;
          if (val === 0) {
            return (
              <span
                style={{
                  background: "#f0fdf4",
                  color: "#16a34a",
                  padding: "2px 8px",
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                Agreed (PKR 0)
              </span>
            );
          }
          return (
            <span
              style={{
                background: "#fff1f2",
                color: "#be123c",
                padding: "2px 8px",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "12px",
              }}
            >
              PKR {val.toLocaleString()}
            </span>
          );
        },
      },
      {
        headerName: "Match Status",
        field: "matchStatus",
        colId: "matchStatus",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => <StatusChip status={p.value} type={getMatchChipType(p.value)} />,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.vendorName && row.vendorName.toLowerCase().includes(q)) ||
          (row.poReference && row.poReference.toLowerCase().includes(q)) ||
          (row.grnReference && row.grnReference.toLowerCase().includes(q)) ||
          (row.vendorInvoiceNo && row.vendorInvoiceNo.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.matchStatus !== statusFilter) {
        return false;
      }

      if (vendorFilter !== "all" && row.vendorName !== vendorFilter) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, vendorFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Three_Way_Matching_Audit_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Three-way matching report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL INVOICES AUDITED"
            value={`${kpis.totalAudited} Sets`}
            subtitle="PO vs GRN vs Vendor Bill verified"
            icon={FiCheckSquare}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="FULL THREE-WAY MATCH"
            value={`${kpis.fullMatchCount} (${kpis.matchPercentage}%)`}
            subtitle="Cleared for accounts payable release"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="QUANTITY DISCREPANCIES"
            value={`${kpis.qtyDiscrepancyCount} Alerts`}
            subtitle="Billed qty exceeds site GRN receipt"
            icon={FiAlertTriangle}
            color="#d97706"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RATE / PRICE VARIANCE"
            value={`${kpis.rateAlertCount} Alerts`}
            subtitle="Billed rate exceeds agreed PO rate"
            icon={FiAlertOctagon}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="3-Way Matching Audit"
            >
              3-Way Matching Audit
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search vendor, PO, GRN, bill..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Match Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Match Statuses</option>
                  <option value="Full Match">Full Match</option>
                  <option value="Quantity Discrepancy">Quantity Discrepancy</option>
                  <option value="Rate Variance Alert">Rate Variance Alert</option>
                </select>
                <label>Match Status</label>
              </div>
            </div>

            {/* Vendor Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                >
                  <option value="all">All Vendors</option>
                  {uniqueVendors.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <label>Vendor</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeWayMatchingAuditReport;
